import { createAlttvisionApiClient, type ApiChannelInvoker } from '@shared/ipc/api-client';
import type { IpcChannel, IpcRequest, IpcResponse } from '@shared/ipc/contracts';
import { decodeIpcWireValue, encodeIpcWireValue } from '@shared/ipc/wire-serialization';
import type { AlttvisionApi } from '@shared/types';

export interface WebConsoleSession {
  id: string;
  userId: string;
  email: string;
  expiresAt: string;
}

export interface WebConsoleSessionResponse {
  authenticated: boolean;
  session: WebConsoleSession | null;
}

export class WebConsoleApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = 'WebConsoleApiError';
  }
}

let webConsoleApi: AlttvisionApi | null = null;

const sessionPersistenceMessage =
  'Autentificarea a reusit, dar browserul nu a pastrat sesiunea. Deschide consola prin https://console.alttvision.local si incearca din nou.';

export function isWebConsoleMode(): boolean {
  return Boolean(import.meta.env.VITE_ALTTVISION_API_BASE_URL);
}

export function getWebConsoleApiBaseUrl(): string {
  const configured = import.meta.env.VITE_ALTTVISION_API_BASE_URL;
  if (!configured) {
    throw new Error('Web console API base URL is not configured.');
  }

  return configured.replace(/\/+$/, '');
}

export function isUnauthorizedWebConsoleError(error: unknown): boolean {
  return error instanceof WebConsoleApiError && error.status === 401;
}

async function readJsonResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return null;
  }

  return JSON.parse(text) as unknown;
}

async function requestJson(path: string, init: RequestInit = {}): Promise<unknown> {
  let response: Response;

  try {
    response = await fetch(`${getWebConsoleApiBaseUrl()}${path}`, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {})
      }
    });
  } catch {
    throw new WebConsoleApiError(
      'API-ul consolei nu poate fi contactat. Verifica domeniile locale HTTPS si proxy-ul Apache.',
      0
    );
  }

  const body = await readJsonResponse(response);

  if (!response.ok) {
    const message =
      body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
        ? body.message
        : `API request failed with ${response.status}.`;
    throw new WebConsoleApiError(message, response.status);
  }

  return body;
}

export async function getWebConsoleSession(): Promise<WebConsoleSessionResponse> {
  return (await requestJson('/auth/session')) as WebConsoleSessionResponse;
}

export async function loginWebConsole(email: string, password: string): Promise<WebConsoleSessionResponse> {
  await requestJson('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });

  const session = await getWebConsoleSession();
  if (!session.authenticated) {
    throw new WebConsoleApiError(sessionPersistenceMessage, 401);
  }

  return session;
}

export async function logoutWebConsole(): Promise<WebConsoleSessionResponse> {
  return (await requestJson('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({})
  })) as WebConsoleSessionResponse;
}

function saveTextFileInBrowser(payload: {
  defaultFileName: string;
  content: string;
}): { saved: boolean; filePath: string | null } {
  const blob = new Blob([payload.content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = payload.defaultFileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  return {
    saved: true,
    filePath: null
  };
}

function createWebInvoke(): ApiChannelInvoker {
  return async <C extends IpcChannel>(
    channel: C,
    ...args: IpcRequest<C> extends void ? [] : [IpcRequest<C>]
  ): Promise<IpcResponse<C>> => {
    const body = await requestJson('/api/invoke', {
      method: 'POST',
      body: JSON.stringify({
        channel,
        payload: encodeIpcWireValue(args[0])
      })
    });

    if (!body || typeof body !== 'object' || !('ok' in body)) {
      throw new Error('Invalid API response.');
    }

    if (body.ok !== true) {
      const message =
        'error' in body &&
        body.error &&
        typeof body.error === 'object' &&
        'message' in body.error &&
        typeof body.error.message === 'string'
          ? body.error.message
          : 'API channel failed.';
      throw new Error(message);
    }

    return decodeIpcWireValue('result' in body ? body.result : undefined) as IpcResponse<C>;
  };
}

export function getWebConsoleApi(): AlttvisionApi {
  if (webConsoleApi) {
    return webConsoleApi;
  }

  const api = createAlttvisionApiClient(createWebInvoke());
  const getProjectRuntimeStatus = api.projects.getProjectRuntimeStatus;

  api.system.openExternal = async (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  api.system.saveTextFile = async (payload) => saveTextFileInBrowser(payload);
  api.system.readClipboardText = async () => navigator.clipboard?.readText() ?? '';
  api.system.writeClipboardText = async (payload) => {
    await navigator.clipboard?.writeText(payload.text);
  };
  api.settings.pickDirectory = async () => null;
  api.projects.openProjectLocalUrl = async (projectId) => {
    const status = await getProjectRuntimeStatus(projectId);
    if (status.localUrl) {
      window.open(status.localUrl, '_blank', 'noopener,noreferrer');
    }
  };

  webConsoleApi = api;
  return api;
}
